import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TextField, Button, Typography, CircularProgress, Alert, Box, Checkbox, FormControlLabel, Select, MenuItem, InputLabel, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Switch } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import axios from 'axios';
import Cookies from 'js-cookie';

// Default form fields for new form (before save)
const defaultFormFields = [
    { id: 0, formId: 0, fieldName: 'Name', fieldType: 'String', enableStatus: 1, requiredStatus: 1 },
    { id: 0, formId: 0, fieldName: 'Email', fieldType: 'Email', enableStatus: 1, requiredStatus: 1 },
    { id: 0, formId: 0, fieldName: 'Phone', fieldType: 'String', enableStatus: 1, requiredStatus: 1 },
    { id: 0, formId: 0, fieldName: 'Message', fieldType: 'Text', enableStatus: 1, requiredStatus: 1 }
];

export default function FormInfo() {
    // ...existing state...
    const { formId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(!!formId);
    const [alert, setAlert] = React.useState(null);

    // Form state
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [eSends, setESends] = React.useState(0);
    const [eSendsAfter, setESendsAfter] = React.useState(1);
    const [eFails, setEFails] = React.useState(0);
    const [eFailsAfter, setEFailsAfter] = React.useState(1);
    const [enableStatus, setEnableStatus] = React.useState(0);
    const [liveChatSupport, setLiveChatSupport] = React.useState(0);
    const [formBeforeChat, setFormBeforeChat] = React.useState(0);
    const [enableAfter, setEnableAfter] = React.useState(1);
    // Rate limit state
    const [eRateLimit, setERateLimit] = React.useState(0);
    const [maxRateLimit, setMaxRateLimit] = React.useState(1);
    const [requestMessage, setRequestMessage] = React.useState('');
    const [successMessage, setSuccessMessage] = React.useState('');
    const [triggerString, setTriggerString] = React.useState('');
    const [triggerWords, setTriggerWords] = React.useState([]);
    const [triggerDialogOpen, setTriggerDialogOpen] = React.useState(false);
    const [newTriggerWord, setNewTriggerWord] = React.useState('');
    const [handoverEmails, setHandoverEmails] = React.useState('');
    const [formFields, setFormFields] = React.useState([]);
    const [nameError, setNameError] = React.useState('');

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [fieldToDelete, setFieldToDelete] = React.useState(null);
    const handleDeleteFieldClick = (field) => {
        if (!formId || formId === 0) {
            setAlert({ severity: 'warning', message: 'Please save the form before deleting form fields.' });
            return;
        }
        setFieldToDelete(field);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setFieldToDelete(null);
    };

    const handleConfirmDeleteField = async () => {
        if (!fieldToDelete) return;
        try {
            await axios.post(`/formfields/delete?id=${fieldToDelete.id}`);
            setFormFields(formFields.filter(f => f.id !== fieldToDelete.id));
            setAlert({ severity: 'success', message: 'Field deleted successfully.' });
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to delete field.' });
        } finally {
            setDeleteDialogOpen(false);
            setFieldToDelete(null);
        }
    };

    // Add Form Field dialog state
    const [addFieldDialogOpen, setAddFieldDialogOpen] = React.useState(false);
    const [newFieldName, setNewFieldName] = React.useState('');
    const [newFieldType, setNewFieldType] = React.useState('String');
    const [newEnableStatus, setNewEnableStatus] = React.useState(1);
    const [newRequiredStatus, setNewRequiredStatus] = React.useState(0);
    const [addFieldLoading, setAddFieldLoading] = React.useState(false);
    const [editFieldId, setEditFieldId] = React.useState(null);

    const handleOpenAddFieldDialog = () => {
        if (!formId) {
            setAlert({ severity: 'warning', message: 'Please save the form before adding form fields.' });
            return;
        }
        setEditFieldId(null);
        setNewFieldName('');
        setNewFieldType('String');
        setNewEnableStatus(1);
        setNewRequiredStatus(0);
        setAddFieldDialogOpen(true);
    };

    const handleOpenEditFieldDialog = (field) => {
        if (!formId || formId === 0) {
            setAlert({ severity: 'warning', message: 'Please save the form before editing form fields.' });
            return;
        }
        setEditFieldId(field.id);
        setNewFieldName(field.fieldName);
        setNewFieldType(field.fieldType);
        setNewEnableStatus(field.enableStatus);
        setNewRequiredStatus(field.requiredStatus);
        setAddFieldDialogOpen(true);
    };

    const handleCloseAddFieldDialog = () => {
        setAddFieldDialogOpen(false);
    };

    const handleAddField = async () => {
        if (!newFieldName.trim()) {
            setAlert({ severity: 'error', message: 'Field Name is required.' });
            return;
        }
        setAddFieldLoading(true);
        try {
            if (editFieldId) {
                // Edit mode
                const payload = {
                    id: editFieldId,
                    formId: formId,
                    fieldName: newFieldName,
                    fieldType: newFieldType,
                    enableStatus: newEnableStatus,
                    requiredStatus: newRequiredStatus
                };
                const response = await axios.post('/formfields/update', payload);
                setFormFields(formFields.map(f => f.id === editFieldId ? response.data : f));
                setAlert({ severity: 'success', message: 'Field updated successfully.' });
            } else {
                // Add mode
                const payload = {
                    id: null,
                    formId: formId,
                    fieldName: newFieldName,
                    fieldType: newFieldType,
                    enableStatus: newEnableStatus,
                    requiredStatus: newRequiredStatus
                };
                const response = await axios.post('/formfields/create', payload);
                setFormFields([...formFields, response.data]);
                setAlert({ severity: 'success', message: 'Field added successfully.' });
            }
            setAddFieldDialogOpen(false);
        } catch (err) {
            setAlert({ severity: 'error', message: editFieldId ? 'Failed to update field.' : 'Failed to add field.' });
        } finally {
            setAddFieldLoading(false);
            setEditFieldId(null);
        }
    };

    // Fetch form if editing
    React.useEffect(() => {
        if (formId) {
            const fetchForm = async () => {
                try {
                    setLoading(true);
                    const response = await axios.post(`/form/get?id=${formId}`);
                    setName(response.data.name || '');
                    setDescription(response.data.description || '');
                    setRequestMessage(response.data.requestMessage || '');
                    setSuccessMessage(response.data.successMessage || '');
                    setESends(response.data.eSends === 1 ? 1 : 0);
                    setESendsAfter(response.data.eSendsAfter || 1);
                    setEFails(response.data.eFails === 1 ? 1 : 0);
                    setEFailsAfter(response.data.eFailsAfter || 1);
                    setEnableStatus(response.data.enableStatus === 1 ? 1 : 0);
                    setLiveChatSupport(response.data.liveChatSupport === 1 ? 1 : 0);
                    setFormBeforeChat(response.data.formBeforeChat === 1 ? 1 : 0);
                    setEnableAfter(response.data.enableAfter || 1);
                    setERateLimit(response.data.eRateLimit === 1 ? 1 : 0);
                    setMaxRateLimit(response.data.maxRateLimit || 1);
                    setTriggerString(response.data.triggerString || '');
                    setTriggerWords((response.data.triggerString || '').split(',').filter(Boolean));
                    setHandoverEmails(response.data.handoverEmails || '');
                    setFormFields(response.data.formFields || []);
                    setAlert(null);
                } catch (err) {
                    setAlert({ severity: 'error', message: 'Failed to load form.' });
                } finally {
                    setLoading(false);
                }
            };
            fetchForm();
        }
    }, [formId]);

    const checkFormExists = async (name) => {
        if (!name.trim()) {
            setNameError('');
            return;
        }

        try {
            const response = await axios.post('/form/formExists', {
                name: name,
                id: formId || null
            });

            if (response.data.exists) {
                setNameError('Form name already exists');
            } else {
                setNameError('');
            }
        } catch (error) {
            console.error('Error checking form name:', error);
        }
    };

    // Helper to get the fields to send (default if new, otherwise current)
    const getFieldsForSave = () => {
        if (!formId && formFields.length === 0) {
            // Remove id/formId for new fields
            return defaultFormFields.map(({ id, formId, ...rest }) => ({ ...rest }));
        }
        // Remove id/formId for new fields if any
        return formFields.map(({ id, formId, ...rest }) => ({ ...rest }));
    };

    // Handle save (create or update)
    const handleSave = async () => {
        if (!name.trim()) {
            setAlert({ severity: 'error', message: 'Form Name is required.' });
            return;
        }
        if (nameError) {
            setAlert({ severity: 'error', message: 'Please fix the form name error' });
            return;
        }
        try {
            setLoading(true);
            const payload = {
                name: name.trim(),
                description,
                requestMessage,
                successMessage,
                eSends: eSends ? 1 : 0,
                eSendsAfter,
                eFails: eFails ? 1 : 0,
                eFailsAfter,
                eRateLimit: eRateLimit ? 1 : 0,
                maxRateLimit,
                enableStatus: enableStatus ? 1 : 0,
                liveChatSupport: liveChatSupport ? 1 : 0,
                formBeforeChat: (formBeforeChat == null || formBeforeChat === 0) ? 0 : 1,
                enableAfter,
                triggerString: triggerWords.join(','),
                handoverEmails,
                formFields: getFieldsForSave()
            };
            if (formId) {
                const response = await axios.post('/form/update', { id: formId, ...payload });
                setAlert({ severity: 'success', message: 'Form updated successfully.' });
                if (response.data) {
                    setFormFields(response.data.formFields || []);
                }
            } else {
                const response = await axios.post('/form/create', payload);
                const data = response.data;
                setAlert({ severity: 'success', message: 'Form created successfully.' });
                if (data) {
                    if (data.id) {
                        setTimeout(() => {
                            setAlert(null);
                            navigate(`/FormInfo/${data.id}`);
                        }, 1500);
                    }
                    setName(data.name || '');
                    setDescription(data.description || '');
                    setRequestMessage(data.requestMessage || '');
                    setSuccessMessage(data.successMessage || '');
                    setESends(data.eSends === 1 ? 1 : 0);
                    setESendsAfter(data.eSendsAfter || 1);
                    setEFails(data.eFails === 1 ? 1 : 0);
                    setEFailsAfter(data.eFailsAfter || 1);
                    setERateLimit(data.eRateLimit === 1 ? 1 : 0);
                    setMaxRateLimit(data.maxRateLimit || 1);
                    setEnableStatus(data.enableStatus === 1 ? 1 : 0);
                    setLiveChatSupport(data.liveChatSupport === 1 ? 1 : 0);
                    setFormBeforeChat(data.formBeforeChat === 1 ? 1 : 0);
                    setEnableAfter(data.enableAfter || 1);
                    setTriggerString(data.triggerString || '');
                    setTriggerWords((data.triggerString || '').split(',').filter(Boolean));
                    setHandoverEmails(data.handoverEmails || '');
                    setFormFields(data.formFields || []);
                }
            }
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to save form.' });
        } finally {
            setLoading(false);
        }
    };

    // Trigger word dialog handlers
    const handleOpenTriggerDialog = () => {
        setNewTriggerWord('');
        setTriggerDialogOpen(true);
    };
    const handleCloseTriggerDialog = () => {
        setTriggerDialogOpen(false);
    };
    const handleAddTriggerWord = () => {
        const trimmed = newTriggerWord.trim();
        if (trimmed && !triggerWords.includes(trimmed)) {
            setTriggerWords([...triggerWords, trimmed]);
        }
        setTriggerDialogOpen(false);
    };
    const handleRemoveTriggerWord = (word) => {
        setTriggerWords(triggerWords.filter(w => w !== word));
    };

    // Show default fields if formId is not set and formFields is empty
    const displayedFormFields = !formId && formFields.length === 0 ? defaultFormFields : formFields;

    const [nameTouched, setNameTouched] = React.useState(false);

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 1 }}>
            <Typography variant="h5" gutterBottom>
                {formId ? 'Edit Form' : 'Create A Form'}
            </Typography>
            <label style={{ display: 'block', marginBottom: 8 }}>
                Forms can be created and used for lead generation, live agent handoff, and other data collection functions.
            </label>
            {alert && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}
            {loading ? (
                <CircularProgress />
            ) : (
                <form>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '40% 60%' }, gap: 3 }}>
                        {/* Left column */}
                        <Box>
                            <TextField
                                label="Form Name"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setNameError('');
                                }}
                                onBlur={(e) => {
                                    setNameTouched(true);
                                    checkFormExists(e.target.value);
                                }}
                                fullWidth
                                margin="normal"
                                required
                                error={nameTouched && (!name.trim() || !!nameError)}
                                helperText={nameTouched ? (!name.trim() ? 'Form Name is required.' : nameError) : ''}
                            />
                            <TextField
                                label="Message To User When Form Triggers"
                                value={requestMessage}
                                onChange={e => setRequestMessage(e.target.value)}
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 200 }}
                                helperText={`${requestMessage.length}/200`}
                            />
                            <TextField
                                label="Message To User After Form Submission"
                                value={successMessage}
                                onChange={e => setSuccessMessage(e.target.value)}
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 200 }}
                                helperText={`${successMessage.length}/200`}
                            />
                            <TextField
                                label="Form Data Is Emailed To (use comma to separate multiple email addresses)"
                                value={handoverEmails}
                                onChange={e => setHandoverEmails(e.target.value)}
                                fullWidth
                                margin="normal"
                                placeholder="email1@example.com, email2@example.com"
                            />
                        </Box>
                        {/* Right column */}

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!liveChatSupport}
                                            onChange={e => setLiveChatSupport(e.target.checked ? 1 : 0)}
                                            color="primary"
                                        />
                                    }
                                    label="Live Chat"
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!formBeforeChat}
                                            onChange={e => setFormBeforeChat(e.target.checked ? 1 : 0)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Box>Collect Information Before Starting Live Chat</Box>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.1, color: 'text.secondary' }}>
                                                If Enabled, the agent gathers key details before routing
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                         
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!eFails}
                                            onChange={e => setEFails(e.target.checked ? 1 : 0)}
                                            color="primary"
                                        />
                                    }
                                    label="Trigger form if agent fails to answer"
                                />
                                <Select
                                    value={eFailsAfter}
                                    onChange={e => setEFailsAfter(Number(e.target.value))}
                                    size="small"
                                    disabled={!eFails}
                                    sx={{ minWidth: 80 }}
                                >
                                    {[...Array(10)].map((_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="body2"> messages, in a row or,</Typography>
                            </Box>
                            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!eRateLimit}
                                            onChange={e => setERateLimit(e.target.checked ? 1 : 0)}
                                            color="primary"
                                        />
                                    }
                                    label="Rate Limit"
                                />
                                <Select
                                    value={maxRateLimit}
                                    onChange={e => setMaxRateLimit(Number(e.target.value))}
                                    size="small"
                                    disabled={!eRateLimit}
                                    sx={{ minWidth: 80 }}
                                >
                                    {Array.from({ length: 10 }, (_, i) => 10 + i * 5).map(val => (
                                        <MenuItem key={val} value={val}>{val}</MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="body2">max messages</Typography>
                            </Box> */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, ml: 4 }}>
                                <label>Trigger form if user types one of the trigger words:</label>
                            </Box>
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Button variant="outlined" onClick={handleOpenTriggerDialog} sx={{ mb: 1, alignSelf: 'flex-start' }}>
                                    Add Trigger Words
                                </Button>
                                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: 2, rowGap: 1, columnGap: 2 }}>
                                    {triggerWords.map(word => (
                                        <Chip
                                            key={word}
                                            label={word}
                                            onDelete={() => handleRemoveTriggerWord(word)}
                                            sx={{ mb: 1, mr: 1 }}
                                        />
                                    ))}
                                </Stack>
                                <Dialog open={triggerDialogOpen} onClose={handleCloseTriggerDialog}>
                                    <DialogTitle>Add Trigger Word</DialogTitle>
                                    <DialogContent>
                                        <TextField
                                            autoFocus
                                            margin="dense"
                                            label="Trigger Word"
                                            fullWidth
                                            value={newTriggerWord}
                                            onChange={e => setNewTriggerWord(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTriggerWord(); } }}
                                        />
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCloseTriggerDialog}>Cancel</Button>
                                        <Button onClick={handleAddTriggerWord} variant="contained">Add</Button>
                                    </DialogActions>
                                </Dialog>
                            </Box>
                        </Box>
                    </Box>
                    {/* Add Form Field and Table below the two columns */}
                    <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gridColumn: '1 / span 2' }}>
                        <Button variant="outlined" onClick={handleOpenAddFieldDialog} sx={{ mb: 1, alignSelf: 'flex-start' }}>
                            Add Form Field
                        </Button>
                        <Dialog open={addFieldDialogOpen} onClose={handleCloseAddFieldDialog}>
                            <DialogContent>
                                <TextField
                                    label="Field Name"
                                    value={newFieldName}
                                    onChange={e => setNewFieldName(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    required
                                />
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="field-type-label">Field Type</InputLabel>
                                    <Select
                                        labelId="field-type-label"
                                        value={newFieldType}
                                        label="Field Type"
                                        onChange={e => setNewFieldType(e.target.value)}
                                    >
                                        <MenuItem value="String">String</MenuItem>
                                        <MenuItem value="Email">Email</MenuItem>
                                        <MenuItem value="Text">Text</MenuItem>
                                        <MenuItem value="Number">Number</MenuItem>
                                    </Select>
                                </FormControl>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!newEnableStatus}
                                                onChange={e => setNewEnableStatus(e.target.checked ? 1 : 0)}
                                                color="primary"
                                            />
                                        }
                                        label="Enable"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!newRequiredStatus}
                                                onChange={e => setNewRequiredStatus(e.target.checked ? 1 : 0)}
                                                color="primary"
                                            />
                                        }
                                        label="Required"
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseAddFieldDialog}>Cancel</Button>
                                <Button onClick={handleAddField} variant="contained" disabled={addFieldLoading}>
                                    {addFieldLoading ? 'Saving...' : 'Save'}
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <TableContainer component={Paper}>
                            <Table size="small" aria-label="form fields table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Field Name</TableCell>
                                        <TableCell>Field Type</TableCell>
                                        <TableCell>Enable </TableCell>
                                        <TableCell>Required </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedFormFields.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No fields found</TableCell>
                                        </TableRow>
                                    ) : (
                                        displayedFormFields.map(field => (
                                            <TableRow key={field.id}>
                                                <TableCell>{field.fieldName}</TableCell>
                                                <TableCell>{field.fieldType}</TableCell>
                                                <TableCell>
                                                    {field.enableStatus === 1 ? (
                                                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                                                    ) : (
                                                        <FiberManualRecordIcon sx={{ color: 'error.main', fontSize: 18 }} />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {field.requiredStatus === 1 ? (
                                                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                                                    ) : (
                                                        <FiberManualRecordIcon sx={{ color: 'error.main', fontSize: 18 }} />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton color="primary" onClick={() => handleOpenEditFieldDialog(field)} size="small" sx={{ mr: 1 }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton color="error" onClick={() => handleDeleteFieldClick(field)} size="small">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                                {/* Delete Confirmation Dialog for Form Field */}
                                <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                                    <DialogTitle>Confirm Delete</DialogTitle>
                                    <DialogContent>
                                        Are you sure you want to delete this field? This action cannot be undone.
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                                        <Button onClick={handleConfirmDeleteField} color="error" variant="contained">Delete</Button>
                                    </DialogActions>
                                </Dialog>
                            </Table>
                        </TableContainer>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button variant="contained" color="primary" onClick={handleSave}>
                            {formId ? 'Update' : 'Save & Create'}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/FormsList')}>
                            Cancel
                        </Button>
                    </Box>
                </form>
            )}
        </Box>
    );
}